// Single entry point for every transactional email the site sends.
//
// Preferred path is the same-origin `/api/send-email` proxy, which injects the
// Brevo API key server-side (nginx template in production, api/send-email.js on
// Vercel). That keeps the key out of the JavaScript bundle — the previous
// client-side call embedded `REACT_APP_BREVO_API_KEY` in main.js, and when the
// build arg was missing it shipped `undefined` as the key, so every submission
// came back 401 and the contact form showed "Failed to send message".
//
// If the proxy is not deployed on a given host, we fall back to the old direct
// call when a build-time key is present, so existing deployments keep working.

const PROXY_ENDPOINT = "/api/send-email";
const BREVO_ENDPOINT = "https://api.brevo.com/v3/smtp/email";

const DEFAULT_SENDER = { name: "Terces Solutions", email: "no-reply@terces.io" };
const DEFAULT_RECIPIENT = [{ email: "athammu@terces.io", name: "Terces Solutions" }];

// The proxy is not reachable on this host — try the direct call instead.
const ENDPOINT_ABSENT = Symbol("email-proxy-absent");

export class EmailNotConfiguredError extends Error {
  constructor() {
    super(
      "Email service is not configured. Set BREVO_API_KEY on the server (or REACT_APP_BREVO_API_KEY at build time)."
    );
    this.name = "EmailNotConfiguredError";
  }
}

function buildPayload({ subject, htmlContent, to, replyTo, attachment, sender }) {
  return {
    sender: sender || DEFAULT_SENDER,
    to: to || DEFAULT_RECIPIENT,
    subject,
    htmlContent,
    ...(replyTo ? { replyTo } : {}),
    ...(attachment ? { attachment } : {}),
  };
}

async function readErrorMessage(response) {
  try {
    const body = await response.json();
    return body?.message || body?.error || "";
  } catch (_) {
    return "";
  }
}

async function sendViaProxy(payload) {
  let response;
  try {
    response = await fetch(PROXY_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (_) {
    // Network error: could be an offline visitor or a host without the proxy.
    return ENDPOINT_ABSENT;
  }

  // A host without the proxy answers with the SPA shell (HTML, status 200) or a
  // bare 404/405 — neither means the send genuinely failed.
  const contentType = response.headers.get("content-type") || "";
  if (
    response.status === 404 ||
    response.status === 405 ||
    !contentType.includes("json")
  ) {
    return ENDPOINT_ABSENT;
  }

  if (!response.ok) {
    const detail = await readErrorMessage(response);
    throw new Error(detail || `Email service responded with ${response.status}`);
  }

  return true;
}

async function sendDirect(payload) {
  const apiKey = process.env.REACT_APP_BREVO_API_KEY;
  if (!apiKey) throw new EmailNotConfiguredError();

  const response = await fetch(BREVO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "api-key": apiKey,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const detail = await readErrorMessage(response);
    throw new Error(detail || `Email service responded with ${response.status}`);
  }

  return true;
}

export async function sendTransactionalEmail(options) {
  const payload = buildPayload(options);

  const proxied = await sendViaProxy(payload);
  if (proxied !== ENDPOINT_ABSENT) return proxied;

  return sendDirect(payload);
}

// Escape user-supplied values before dropping them into an HTML email body.
export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
